import { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import Statscard from '../components/statscard';
import './dashboard.css';
import { supabase } from '../supabase';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import calendarIcon from '../assets/calendar.svg';
import ticketIcon from '../assets/ticket.svg';
import usersIcon from '../assets/users.svg';
import moneyIcon from '../assets/money.svg';
import ratingIcon from '../assets/rating.svg';
import dropdownIcon from '../assets/dropdown.svg';

const formatDateTimeLocal = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return localDate.toISOString().slice(0, 16);
};

const Dashboard = () => {
  const [programs, setPrograms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [eventSettings, setEventSettings] = useState(null);
  const [eventSettingsForm, setEventSettingsForm] = useState({
    event_name: '',
    event_start_date: '',
    event_end_date: '',
    booking_start_date: '',
    booking_end_date: '',
    available_days: '',
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const getTicket = (booking) => {
    return Array.isArray(booking.tickets) ? booking.tickets[0] : booking.tickets;
  };

  const fetchDashboardData = useCallback(async () => {
    const programsRes = await supabase
      .from('programs')
      .select(`
        id,
        title_en,
        created_at,
        category_id,
        categories(name_en)
      `);

    const bookingsRes = await supabase
      .from('bookings')
      .select(`
        id,
        created_at,
        ticket_id,
        full_name,
        email,
        tickets(type, price)
      `);

    const usersRes = await supabase
      .from('users')
      .select('id, created_at');

    setPrograms(programsRes.data || []);
    setBookings(bookingsRes.data || []);
    setUsers(usersRes.data || []);
  }, []);

  const fetchEventSettings = useCallback(async () => {
    const { data } = await supabase
      .from('event_settings')
      .select('*')
      .limit(1)
      .single();

    if (!data) {
      return;
    }

    setEventSettings(data);
    setEventSettingsForm({
      event_name: data.event_name || '',
      event_start_date: formatDateTimeLocal(data.event_start_date),
      event_end_date: formatDateTimeLocal(data.event_end_date),
      booking_start_date: formatDateTimeLocal(data.booking_start_date),
      booking_end_date: formatDateTimeLocal(data.booking_end_date),
      available_days: (data.available_days || []).join('\n'),
    });
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchEventSettings();
  }, [fetchDashboardData, fetchEventSettings]);

  const updateEventSettingsField = (field, value) => {
    setEventSettingsForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveEventSettings = async (e) => {
    e.preventDefault();

    if (!eventSettings) {
      return;
    }

    setSettingsSaving(true);
    setSettingsMessage('');

    const availableDays = eventSettingsForm.available_days
      .split('\n')
      .map((day) => day.trim())
      .filter(Boolean);

    const { error } = await supabase
      .from('event_settings')
      .update({
        event_name: eventSettingsForm.event_name,
        event_start_date: new Date(eventSettingsForm.event_start_date).toISOString(),
        event_end_date: new Date(eventSettingsForm.event_end_date).toISOString(),
        booking_start_date: new Date(eventSettingsForm.booking_start_date).toISOString(),
        booking_end_date: new Date(eventSettingsForm.booking_end_date).toISOString(),
        available_days: availableDays,
      })
      .eq('id', eventSettings.id);

    setSettingsSaving(false);
    setSettingsMessage(error ? 'Settings could not be saved' : 'Settings saved');

    if (!error) {
      await fetchEventSettings();
    }
  };

  const percentageChange = (current, previous) => {
    const change = ((current - previous) / previous) * 100;
    const sign = change >= 0 ? '+' : '';
    return Number.isFinite(change) ? `${sign}${change.toFixed(1)}%` : '+0.0%';
  };

  const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const monthlyData = [];
  for (let offset = 5; offset >= 0; offset--) {
    const date = new Date(currentMonthStart);
    date.setMonth(currentMonthStart.getMonth() - offset);

    monthlyData.push({
      key: `${date.getFullYear()}-${date.getMonth()}`,
      month: date.toLocaleString('en-US', { month: 'short' }),
      revenue: 0,
      bookings: 0,
      users: 0,
    });
  }

  bookings.forEach((booking) => {
    const date = new Date(booking.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const monthItem = monthlyData.find((item) => item.key === key);

    if (monthItem) {
      monthItem.bookings += 1;
      monthItem.revenue += Number(getTicket(booking)?.price) || 0;
    }
  });

  users.forEach((user) => {
    const date = new Date(user.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const monthItem = monthlyData.find((item) => item.key === key);

    if (monthItem) {
      monthItem.users += 1;
    }
  });

  const categoryMap = {};
  programs.forEach((program) => {
    const categoryName = program.categories?.name_en;
    if (categoryName) {
      categoryMap[categoryName] = (categoryMap[categoryName] || 0) + 1;
    }
  });

  const chartColors = ['#4299D5', '#6AAFD4', '#96B5C9', '#A8C2D3', '#D5E4EF'];

  const categoryData = Object.entries(categoryMap)
    .map(([name, value], index) => ({
      name,
      value,
      fill: chartColors[index % chartColors.length],
    }))
    .sort((a, b) => b.value - a.value);

  const recentBookings = [...bookings]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  const ticketBookingMap = {};
  bookings.forEach((booking) => {
    const ticketType = getTicket(booking)?.type;
    if (ticketType) {
      ticketBookingMap[ticketType] = (ticketBookingMap[ticketType] || 0) + 1;
    }
  });

  const topTickets = Object.entries(ticketBookingMap)
    .map(([name, bookingCount]) => ({
      name,
      bookingCount,
    }))
    .sort((a, b) => b.bookingCount - a.bookingCount)
    .slice(0, 5);

  const totalRevenue = bookings.reduce((sum, booking) => {
    return sum + (Number(getTicket(booking)?.price) || 0);
  }, 0);

  const avgBookingValue = bookings.length ? totalRevenue / bookings.length : 0;

  const currentMonth = monthlyData[monthlyData.length - 1] || { bookings: 0, revenue: 0, users: 0 };
  const previousMonth = monthlyData[monthlyData.length - 2] || { bookings: 0, revenue: 0, users: 0 };

  const stats = [
    {
      label: 'Total Programs',
      value: programs.length.toLocaleString(),
      change: '',
      icon: calendarIcon,
    },
    {
      label: 'Total Bookings',
      value: bookings.length.toLocaleString(),
      change: percentageChange(currentMonth.bookings, previousMonth.bookings),
      icon: ticketIcon,
    },
    {
      label: 'Total Users',
      value: users.length.toLocaleString(),
      change: percentageChange(currentMonth.users, previousMonth.users),
      icon: usersIcon,
    },
    {
      label: 'Revenue',
      value: `$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      change: percentageChange(currentMonth.revenue, previousMonth.revenue),
      icon: moneyIcon,
    },
    {
      label: 'Avg Booking Value',
      value: `$${avgBookingValue.toFixed(2)}`,
      change: '',
      icon: ratingIcon,
    },
  ];

  return (
    <Layout>
      <div className="dashboard_page">
        <Title
          subtitle="Welcome back. Here is what is happening with TECHNE today."
          title="Dashboard"
        />

        <div className="dashboard_stats">
          {stats.map((stat) => (
            <Statscard
              key={stat.label}
              change={stat.change}
              icon={stat.icon}
              label={stat.label}
              value={stat.value}
            />
          ))}
        </div>

        <section className={`dashboard_card dashboard_settings_card${settingsExpanded ? ' dashboard_settings_card_open' : ''}`}>
          <button
            aria-expanded={settingsExpanded}
            className="dashboard_settings_toggle"
            onClick={() => setSettingsExpanded((expanded) => !expanded)}
            type="button"
          >
            <div>
              <h2>Event Settings</h2>
              <span>{eventSettingsForm.event_name || 'Content scheduling'}</span>
            </div>
            <img alt="" src={dropdownIcon} />
          </button>

          {settingsExpanded && (
            <form className="dashboard_settings_form" onSubmit={saveEventSettings}>
              <label className="dashboard_settings_field dashboard_settings_field_full">
                <span>Event Name</span>
                <input
                  onChange={(e) => updateEventSettingsField('event_name', e.target.value)}
                  value={eventSettingsForm.event_name}
                />
              </label>

              <label className="dashboard_settings_field">
                <span>Event Start Date</span>
                <input
                  onChange={(e) => updateEventSettingsField('event_start_date', e.target.value)}
                  type="datetime-local"
                  value={eventSettingsForm.event_start_date}
                />
              </label>

              <label className="dashboard_settings_field">
                <span>Event End Date</span>
                <input
                  onChange={(e) => updateEventSettingsField('event_end_date', e.target.value)}
                  type="datetime-local"
                  value={eventSettingsForm.event_end_date}
                />
              </label>

              <label className="dashboard_settings_field">
                <span>Booking Start Date</span>
                <input
                  onChange={(e) => updateEventSettingsField('booking_start_date', e.target.value)}
                  type="datetime-local"
                  value={eventSettingsForm.booking_start_date}
                />
              </label>

              <label className="dashboard_settings_field">
                <span>Booking End Date</span>
                <input
                  onChange={(e) => updateEventSettingsField('booking_end_date', e.target.value)}
                  type="datetime-local"
                  value={eventSettingsForm.booking_end_date}
                />
              </label>

              <label className="dashboard_settings_field dashboard_settings_field_full">
                <span>Available Days</span>
                <textarea
                  onChange={(e) => updateEventSettingsField('available_days', e.target.value)}
                  rows={3}
                  value={eventSettingsForm.available_days}
                />
              </label>

              <div className="dashboard_settings_actions">
                {settingsMessage && <span>{settingsMessage}</span>}
                <button disabled={settingsSaving || !eventSettings} type="submit">
                  {settingsSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </section>

        <section className="dashboard_card">
          <div className="dashboard_card_header">
            <h2>Revenue Analytics</h2>
            <span>Last 6 months</span>
          </div>

          <div className="dashboard_chart_box">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4299D5" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#4299D5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#4299D5"
                  strokeWidth={3}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="dashboard_chart_grid">
          <div className="dashboard_card">
            <div className="dashboard_card_header">
              <h2>Bookings Trend</h2>
              <span>Monthly bookings</span>
            </div>

            <div className="dashboard_chart_box">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="bookings"
                    name="Bookings"
                    fill="#6AAFD4"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dashboard_card">
            <div className="dashboard_card_header">
              <h2>Programs by Category</h2>
              <span>{programs.length} programs</span>
            </div>

            <div className="dashboard_chart_box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={82}
                    paddingAngle={3}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="dashboard_card">
          <div className="dashboard_card_header">
            <h2>User Growth</h2>
            <span>New users over time</span>
          </div>

          <div className="dashboard_chart_box">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  dataKey="users"
                  name="Users"
                  stroke="#4299D5"
                  strokeWidth={3}
                  dot={{ fill: '#4299D5', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="dashboard_table_card">
          <div className="dashboard_table_header">
            <h2>Recent Bookings</h2>
            <a href="/bookings">View All</a>
          </div>

          <div className="dashboard_table_scroll">
            <table className="dashboard_table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Ticket Type</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>{booking.full_name}</td>
                    <td>{booking.email}</td>
                    <td>
                      <span className="dashboard_chip">{getTicket(booking)?.type}</span>
                    </td>
                    <td>{new Date(booking.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className="dashboard_status dashboard_status_confirmed">
                        {booking.ticket_id ? 'confirmed' : 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dashboard_card dashboard_compact_card">
          <div className="dashboard_card_header">
            <h2>Top Tickets</h2>
            <span>Most booked ticket types</span>
          </div>

          <div className="dashboard_booth_list">
            {topTickets.map((ticket) => (
              <div className="dashboard_booth_item" key={ticket.name}>
                <div>
                  <strong>{ticket.name}</strong>
                  <span>High demand ticket</span>
                </div>
                <em>{ticket.bookingCount} bookings</em>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
